import { useTheme, Box, Grid, Link, Stack, Typography } from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SurePassLogoType from '../../../assets/icons/SurePassLogoType';
import AppStoreBadge from '../../../assets/appleStoreBadge.png';
import PlayStoreBadge from '../../../assets/googleStoreBadge.png';

const APP_STORE_URL = 'https://apps.apple.com/us/app/surepass/id6738395190';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.surepass.app&pcampaignid=web_share';

interface SuccessProps {
  // The email the official registered with
  email?: string;
}

const Success = ({ email }: SuccessProps) => {
  const theme = useTheme();

  const styles = {
    container: {
      backgroundColor: theme.palette.background.default,
      borderRadius: '4px',
    },
    header: {
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
      padding: '32px 24px 16px 24px',
    },
    logo: {
      width: '200px',
      height: '45px',
    },
    content: {
      paddingTop: '32px',
      paddingBottom: '32px',
      marginLeft: '96px',
      marginRight: '96px',
    },
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <SurePassLogoType sx={styles.logo} />
      </Box>
      <Stack
        direction='column'
        sx={[{
          justifyContent: 'center',
          alignItems: 'center'
        }, styles.content]}>
        <CheckCircleOutlineOutlinedIcon color='success' fontSize='large'/>
        <Typography component='h3' variant='xxl' align='center' sx={{
          color: theme.palette.text.primary,
          mt: '16px'
        }}>
          Download the SurePass App
        </Typography>
        <Typography component='h4' variant='m' align='center' sx={{
          color: theme.palette.text.primary,
          mt: '8px'
        }}>
          to your mobile device to get started.
        </Typography>
        <Grid
          container
          sx={{
            justifyContent: 'center',
            mt: '16px',
            gap: 2
          }}>
          {/* Opened in a new tab so the confirmation stays visible */}
          <Link href={APP_STORE_URL} target='_blank' rel='noopener noreferrer'>
            <Box
              component='img'
              src={AppStoreBadge}
              alt='Download on the App Store'
              sx={{
                width: '120px',
                height: '40px'
              }} />
          </Link>
          <Link href={PLAY_STORE_URL} target='_blank' rel='noopener noreferrer'>
            <Box
              component='img'
              src={PlayStoreBadge}
              alt='Get it on Google Play'
              sx={{
                width: '120px',
                height: '40px'
              }} />
          </Link>
        </Grid>
        <Grid
          container
          sx={{
            justifyContent: 'flex-start',
            mt: '32px'
          }}>
          <Typography component='p' variant='m' color='primary'>
            1. Log in to the SurePass App
          </Typography>
          <Typography component='p' color='primary' sx={{ marginLeft: '18px'}}>
            {email
              ? <>Use your email <strong>{email}</strong> to view your Official Capacity Identification (OCID).</>
              : 'Use your email to view your Official Capacity Identification (OCID).'}
          </Typography>
          <Typography component='p' variant='m' color='primary' sx={{
            mt: '16px'
          }}>
            2. Scan Your OCID at SurePass Checkpoints
          </Typography>
          <Typography component='p' color='primary' sx={{ marginLeft: '18px'}}>
            There are 2 different ways to scan your mobile OCID at SurePass Checkpoints.
          </Typography>
          <Grid
            container
            sx={{
              mt: '16px',
              ml: '18px'
            }}>
            <QrCodeIcon sx={{ marginRight: '8px'}} />
            <Box>
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>QR Code</Typography>
              <Typography variant='bodyMedium'>
                SurePass checkpoints have a camera that will scan the QR Code on your OCID.
              </Typography>
            </Box>
          </Grid>
          <Grid
            container
            sx={{
              mt: '16px',
              ml: '18px'
            }}>
            <AccountBalanceWalletIcon sx={{ marginRight: '8px'}} />
            <Box>
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>Mobile Wallet</Typography>
              <Typography variant='bodyMedium'>
                Add your OCID to your mobile wallet and scan at the checkpoint.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Typography variant='bodyMedium' sx={{
          mt: '32px'
        }}>
          <Link href='mailto:help@surepass.com'>Contact Support</Link> with questions or to request help at any time.
        </Typography>
        <Stack
          direction='row'
          sx={{
            gap: 1,
            mt: '16px'
          }}>
          <Typography variant='bodyMedium'><Link href='https://surepass.com/terms'>Terms of Service</Link></Typography>
          <Typography variant='bodyMedium'><Link href='https://surepass.com/privacy/'>Privacy Policy</Link></Typography>
          <Typography variant='bodyMedium'><Link href='mailto:help@surepass.com'>Contact Us</Link></Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Success;